import { useEffect, useState } from 'react';

import styled from '@emotion/styled';
import { useFormik } from 'formik';
import toast from 'react-hot-toast';
import * as Yup from 'yup';

import { PrimaryBlackButton, PrimaryWhiteButton } from '../../../components/Button/Buttons';
import { Card, CardTitle, Hint, Input, Scroll, SimpleTable } from '../../../components/Layout/PagePieces';
import useFreshPasskey from '../../../hooks/auth/passkeys/useFreshPasskey';
import usePricingSettings from '../../../hooks/dashboard/profit/usePricingSettings';
import { PricingKey, PricingSettings, PricingValues } from '../../../models/profit/profit.model';
import { formatNaira, tableFormatDateTimeNG } from '../../../utils/utils';

const LABELS: Record<PricingKey, string> = {
  AIRTIME_MAX_PER_PURCHASE: 'Most airtime per purchase (₦)',
  SERVICE_FEE_CABLE: 'Cable service fee (₦)',
  ELECTRICITY_EXTRA_FEE: 'Extra fee from electricity units (₦)',
  DATA_LOSS_GUARD_ENABLED: 'Data loss guard',
  DATA_MIN_PROFIT: 'A data plan must earn at least (₦)',
  PAYSTACK_FEE_PCT: 'Paystack fee (%)',
  PAYSTACK_FEE_FLAT: 'Paystack flat fee (₦)',
  PAYSTACK_FEE_FLAT_FROM: 'Flat fee from (₦)',
  PAYSTACK_FEE_CAP: 'Paystack fee cap (₦)',
  PAYSTACK_TRANSFER_FEE_PCT: 'Bank transfer fee (%)',
  PAYSTACK_TRANSFER_FEE_CAP: 'Bank transfer fee cap (₦)',
};
const NUMBER_KEYS = (Object.keys(LABELS) as PricingKey[]).filter((k) => k !== 'DATA_LOSS_GUARD_ENABLED');
const NAIRA_KEYS: PricingKey[] = NUMBER_KEYS.filter((k) => k !== 'PAYSTACK_FEE_PCT' && k !== 'PAYSTACK_TRANSFER_FEE_PCT');

const GROUPS: { title: string; hint: string; keys: PricingKey[] }[] = [
  {
    title: 'Airtime',
    hint: 'Below ₦2,500 Paystack takes no flat fee, so airtime up to ₦2,000 always earns.',
    keys: ['AIRTIME_MAX_PER_PURCHASE'],
  },
  { title: 'Cable', hint: 'Added on top of the plan price. ₦110 covers Paystack’s ₦100 and 1.5% on the fee.', keys: ['SERVICE_FEE_CABLE'] },
  {
    title: 'Electricity',
    hint: 'What the customer enters is what their card pays. The card processing fee (Paystack’s fee, rounded up) plus this extra comes out of it, and the rest is units.',
    keys: ['ELECTRICITY_EXTRA_FEE'],
  },
  {
    title: 'Data',
    hint: 'With the guard on, plans that would earn less than this aren’t listed or sold. Customers are told we can’t process that plan right now.',
    keys: ['DATA_LOSS_GUARD_ENABLED', 'DATA_MIN_PROFIT'],
  },
  {
    title: 'Paystack’s card fee',
    hint: 'Change only when Paystack changes its pricing. The electricity fee, the data guard and Unit economics all use it.',
    keys: ['PAYSTACK_FEE_PCT', 'PAYSTACK_FEE_FLAT', 'PAYSTACK_FEE_FLAT_FROM', 'PAYSTACK_FEE_CAP'],
  },
  {
    title: 'Paystack’s bank transfer fee',
    hint: 'For electricity paid by bank transfer into the customer’s own account. It comes out of what they send instead of the card fee.',
    keys: ['PAYSTACK_TRANSFER_FEE_PCT', 'PAYSTACK_TRANSFER_FEE_CAP'],
  },
];

type FormValues = Record<PricingKey, string | boolean>;

const whole = (label: string, min: number, max: number) =>
  Yup.number().typeError('A number').integer('Whole naira').min(min, `At least ${min}`).max(max, `At most ${max.toLocaleString()}`).required(label);

const schema = Yup.object({
  AIRTIME_MAX_PER_PURCHASE: whole('Required', 50, 50000),
  SERVICE_FEE_CABLE: whole('Required', 0, 1000),
  ELECTRICITY_EXTRA_FEE: whole('Required', 0, 1000),
  DATA_MIN_PROFIT: whole('Required', -500, 1000),
  PAYSTACK_FEE_PCT: Yup.number().typeError('A number').min(0).max(10, 'At most 10').required('Required'),
  PAYSTACK_FEE_FLAT: whole('Required', 0, 1000),
  PAYSTACK_FEE_FLAT_FROM: whole('Required', 0, 100000),
  PAYSTACK_FEE_CAP: whole('Required', 1, 100000),
  PAYSTACK_TRANSFER_FEE_PCT: Yup.number().typeError('A number').min(0).max(10, 'At most 10').required('Required'),
  PAYSTACK_TRANSFER_FEE_CAP: whole('Required', 1, 100000),
});

// The same sums the backend does (app/service/pricing.py), for the preview.
const cardFee = (v: FormValues, charged: number) => {
  let fee = (charged * Number(v.PAYSTACK_FEE_PCT)) / 100;
  if (charged >= Number(v.PAYSTACK_FEE_FLAT_FROM)) fee += Number(v.PAYSTACK_FEE_FLAT);
  return Math.min(fee, Number(v.PAYSTACK_FEE_CAP));
};
const electricityFee = (v: FormValues, paid: number) =>
  Math.min(Math.ceil(Math.round(cardFee(v, paid) * 100) / 100) + Number(v.ELECTRICITY_EXTRA_FEE), paid);

const shown = (key: PricingKey, value: string | boolean | undefined) => {
  if (value === undefined) return '';
  if (typeof value === 'boolean') return value ? 'On' : 'Off';
  if (key === 'PAYSTACK_FEE_PCT' || key === 'PAYSTACK_TRANSFER_FEE_PCT') return `${Number(value)}%`;
  return NAIRA_KEYS.includes(key) ? formatNaira(Number(value)) : value;
};

const shownHistory = (key: PricingKey, value: string) =>
  shown(key, key === 'DATA_LOSS_GUARD_ENABLED' ? value === 'True' || value === 'true' : value);

// How each bill is priced (docs/pricing-acceptance-criteria.md P5). Saved
// values apply from the next purchase on; the settings file only holds the
// defaults. Unit economics recalculates from these. Saving is a review step
// (old → new, a reason, a fresh passkey check, and a confirmation for a big
// change, P9).
const PricingCard: React.FC<{ onSaved?: () => void }> = ({ onSaved }) => {
  const { settings, fetchSettings, setSettings } = usePricingSettings();
  const { fresh } = useFreshPasskey();
  const [pending, setPending] = useState<Record<string, string | boolean> | null>(null);
  const [reason, setReason] = useState('');
  const [bigChange, setBigChange] = useState<string | null>(null);
  const [confirmed, setConfirmed] = useState(false);
  const [saving, setSaving] = useState(false);

  const closeReview = () => {
    setPending(null);
    setReason('');
    setBigChange(null);
    setConfirmed(false);
  };

  const submit = async () => {
    if (!pending || !settings) return;
    setSaving(true);
    try {
      const data = await fresh<PricingSettings>({
        url: 'api/v1/admin/pricing-settings',
        method: 'put',
        data: {
          values: pending,
          reason: reason.trim(),
          expected: Object.fromEntries(Object.keys(pending).map((k) => [k, settings.values[k as PricingKey]])),
          confirm_large_change: bigChange != null && confirmed,
        },
      });
      setSettings(data);
      closeReview();
      toast.success('Saved. The next purchase uses these.');
      onSaved?.();
    } catch (err) {
      const message = (err as Error).message;
      if (/big change/.test(message)) setBigChange(message);
      else toast.error(message);
      if (/changed since/.test(message)) fetchSettings();
    } finally {
      setSaving(false);
    }
  };

  useEffect(() => {
    fetchSettings();
    // eslint-disable-next-line
  }, []);

  const formik = useFormik<FormValues>({
    enableReinitialize: true,
    validationSchema: schema,
    initialValues: (settings?.values ?? {}) as PricingValues as unknown as FormValues,
    onSubmit: (v) => {
      const current = settings?.values;
      if (!current) return;
      const changed = Object.fromEntries(
        (Object.keys(LABELS) as PricingKey[])
          .filter((k) => (typeof v[k] === 'boolean' ? v[k] !== current[k] : Number(v[k]) !== Number(current[k])))
          .map((k) => [k, typeof v[k] === 'boolean' ? v[k] : String(v[k])])
      );
      if (!Object.keys(changed).length) {
        toast('Nothing changed');
        return;
      }
      setPending(changed);
    },
  });

  const v = formik.values;

  // A field edited after opening the review closes it, so what's reviewed is
  // always what gets saved.
  useEffect(() => {
    closeReview();
    // eslint-disable-next-line
  }, [v]);
  const ready = settings != null && v.PAYSTACK_FEE_PCT !== undefined;
  const examples = ready
    ? [
        ...[2000, 5000, 20000].map((paid) => {
          const fee = electricityFee(v, paid);
          return `${formatNaira(paid)} of electricity paid → ${formatNaira(paid - fee)} of units + ${formatNaira(fee)} card processing fee`;
        }),
        `A ${formatNaira(5000)} cable plan → ${formatNaira(5000 + Number(v.SERVICE_FEE_CABLE))} charged`,
        ...(settings?.transfer_payments_enabled ? [(() => {
          const fee = Math.min(
            Math.ceil(Math.min((100000 * Number(v.PAYSTACK_TRANSFER_FEE_PCT)) / 100, Number(v.PAYSTACK_TRANSFER_FEE_CAP))) +
              Number(v.ELECTRICITY_EXTRA_FEE),
            100000
          );
          return `${formatNaira(100000)} of electricity by bank transfer → ${formatNaira(100000 - fee)} of units + ${formatNaira(fee)} bank transfer fee`;
        })()] : []),
      ]
    : [];

  return (
    <Card>
      <CardTitle>Pricing</CardTitle>
      <Hint>
        How each bill is priced, used from the next purchase on. Customers see every fee before they enter their PIN.
        {settings && !settings.transfer_payments_enabled && ' Customers pay by card only.'}
        {settings?.updated_at ? ` Last changed ${tableFormatDateTimeNG(settings.updated_at)} by ${settings.updated_by}.` : ''}
      </Hint>
      {ready && (
        <form onSubmit={formik.handleSubmit}>
          {GROUPS.filter((g) => settings?.transfer_payments_enabled || !g.keys.includes('PAYSTACK_TRANSFER_FEE_PCT')).map((group) => (
            <Group key={group.title}>
              <h4>{group.title}</h4>
              <Hint>{group.hint}</Hint>
              <Grid>
                {group.keys.map((key) =>
                  key === 'DATA_LOSS_GUARD_ENABLED' ? (
                    <Toggle key={key}>
                      <input
                        type='checkbox'
                        name={key}
                        checked={Boolean(v[key])}
                        onChange={(e) => formik.setFieldValue(key, e.target.checked)}
                      />
                      <span>{v[key] ? 'Guard on: loss-making plans aren’t sold' : 'Guard off: every plan is sold'}</span>
                    </Toggle>
                  ) : (
                    <Field key={key}>
                      <span>{LABELS[key]}</span>
                      <Input
                        type='number'
                        name={key}
                        step={key === 'PAYSTACK_FEE_PCT' || key === 'PAYSTACK_TRANSFER_FEE_PCT' ? 0.01 : 1}
                        value={v[key] as string}
                        onChange={formik.handleChange}
                      />
                      {formik.errors[key] ? (
                        <ErrorText>{String(formik.errors[key])}</ErrorText>
                      ) : (
                        <small>default {shown(key, settings?.file_defaults[key])}</small>
                      )}
                    </Field>
                  )
                )}
              </Grid>
            </Group>
          ))}
          <Preview>
            <strong>What customers would see</strong>
            {examples.map((e) => (
              <span key={e}>{e}</span>
            ))}
          </Preview>
          {!pending ? (
            <PrimaryBlackButton type='submit'>Review changes</PrimaryBlackButton>
          ) : (
            <ReviewBox>
              <strong>You’re changing</strong>
              {Object.entries(pending).map(([k, value]) => (
                <span key={k}>
                  {LABELS[k as PricingKey]}: {shown(k as PricingKey, settings?.values[k as PricingKey])} →{' '}
                  <strong>{shown(k as PricingKey, value)}</strong>
                </span>
              ))}
              {bigChange && (
                <BigChange>
                  <span>{bigChange}</span>
                  <label>
                    <input type='checkbox' checked={confirmed} onChange={(e) => setConfirmed(e.target.checked)} /> Yes, this is
                    what I mean to change
                  </label>
                </BigChange>
              )}
              <Field>
                <span>Why is it changing?</span>
                <Input value={reason} onChange={(e) => setReason(e.target.value)} maxLength={300} placeholder='e.g. Paystack’s new pricing from 1 Dec' />
              </Field>
              <Buttons>
                <PrimaryBlackButton
                  type='button'
                  onClick={submit}
                  disabled={saving || reason.trim().length < 5 || (bigChange != null && !confirmed)}
                >
                  {saving ? 'Saving…' : 'Confirm with passkey and save'}
                </PrimaryBlackButton>
                <PrimaryWhiteButton type='button' onClick={closeReview}>
                  Back
                </PrimaryWhiteButton>
              </Buttons>
            </ReviewBox>
          )}
        </form>
      )}
      {!!settings?.history.length && (
        <Scroll>
          <SimpleTable>
            <thead>
              <tr>
                <th>Changed</th>
                <th>Setting</th>
                <th className='num'>Was</th>
                <th className='num'>New value</th>
                <th>Why</th>
                <th>By</th>
              </tr>
            </thead>
            <tbody>
              {settings.history.map((h, i) => (
                <tr key={i}>
                  <td>{tableFormatDateTimeNG(h.changed_at)}</td>
                  <td>{LABELS[h.key] ?? h.key}</td>
                  <td className='num'>{h.previous != null ? shownHistory(h.key, h.previous) : '—'}</td>
                  <td className='num'>{shownHistory(h.key, h.value)}</td>
                  <td>{h.reason ?? '—'}</td>
                  <td>{h.changed_by}</td>
                </tr>
              ))}
            </tbody>
          </SimpleTable>
        </Scroll>
      )}
    </Card>
  );
};

export default PricingCard;

const Group = styled.section`
  padding: 12px 0;
  border-bottom: 1px solid var(--line);

  h4 {
    margin: 0 0 4px;
    font-size: 14px;
    font-weight: 600;
  }
  p {
    margin-bottom: 10px;
  }
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
  gap: 12px;
`;

const Field = styled.label`
  display: flex;
  flex-direction: column;
  gap: 4px;
  font-size: 13px;
  color: ${({ theme }) => theme.palette.grayTextColor};

  small {
    font-size: 11px;
  }
`;

const Toggle = styled.label`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 13px;
  cursor: pointer;
`;

const Preview = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  margin: 14px 0;
  padding: 12px 14px;
  border-radius: var(--radius-sm);
  background: var(--panel);
  font-size: 13px;
  font-variant-numeric: tabular-nums;
`;

const ReviewBox = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  padding: 12px 14px;
  border: 1px solid var(--line);
  border-radius: var(--radius-sm);
  font-size: 13px;
`;

const BigChange = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  padding: 8px 10px;
  border: 1px solid var(--red);
  border-radius: var(--radius-sm);

  label {
    display: flex;
    gap: 6px;
    align-items: center;
  }
`;

const Buttons = styled.div`
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
`;

const ErrorText = styled.span`
  color: ${({ theme }) => theme.palette.errorRed};
  font-size: 12px;
`;
